import { useMutation } from "@/api/utils/useQuery"
import { HasuraMutations } from "@/api/hasura/mutations"
import {
  InsertGasTradeMutationVariables,
  InsertGasTradeMutation,
  InsertElecTradeMutationVariables,
  InsertElecTradeMutation,
  ConfirmGasTradeMutationVariables,
  ConfirmGasTradeMutation,
  ConfirmElecTradeMutationVariables,
  ConfirmElecTradeMutation,
  UpdateElecTradeMutation,
  UpdateElecTradeMutationVariables,
  UpdateGasTradeMutation,
  UpdateGasTradeMutationVariables,
  UnlockGasTradeMutationVariables,
  UnlockElecTradeMutationVariables,
  UnlockGasTradeMutation,
  UnlockElecTradeMutation,
  InsertGasTradeHistoryMutation,
  InsertElecTradeHistoryMutation,
  InsertElecTradeHistoryMutationVariables,
  InsertGasTradeHistoryMutationVariables,
  InsertElecNominationsMutation,
  InsertElecNominationsMutationVariables,
} from "@/api/hasura/generated/graphql"
import { formatLocalDateTime } from "@/helpers/dateHelper"
import { MutationResult } from "@/api/utils/types"
import {
  ITradesAddElecData,
  ITradesAddElecHistoryData,
  ITradesConfirmElecData,
} from "@/models/operations/tradingData/elecData.types"
import {
  ITradesAddGasData,
  ITradesAddGasHistoryData,
  ITradesConfirmGasData,
} from "@/models/operations/tradingData/gasData.types"
import dayjs from "dayjs"
import { IElecNominationsData } from "@/models/operations/tradingData/elecNominationsData.types"
import { getHalfHourlyVolume } from "@/helpers/tradeHelper"

type InsertGasTradeMutationResponse =
  InsertGasTradeMutation["insert_GasTrades_one"]

type InsertElecTradeMutationResponse =
  InsertElecTradeMutation["insert_ElecTrades_one"]

type InsertGasTradeHistoryMutationResponse =
  InsertGasTradeHistoryMutation["insert_GasTradesHistory_one"]

type InsertElecTradeHistoryMutationResponse =
  InsertElecTradeHistoryMutation["insert_ElecTradesHistory_one"]

type ConfirmGasTradeMutationResponse =
  ConfirmGasTradeMutation["update_GasTrades_by_pk"]

type ConfirmElecTradeMutationResponse =
  ConfirmElecTradeMutation["update_ElecTrades_by_pk"]

type UpdateGasTradeMutationResponse =
  UpdateGasTradeMutation["update_GasTrades_by_pk"]

type UpdateElecTradeMutationResponse =
  UpdateElecTradeMutation["update_ElecTrades_by_pk"]

type UnlockGasTradeMutationResponse =
  UnlockGasTradeMutation["update_GasTrades_by_pk"]

type UnlockElecTradeMutationResponse =
  UnlockElecTradeMutation["update_ElecTrades_by_pk"]

type InsertElecNominationsMutationResponse =
  InsertElecNominationsMutation["insert_ElecNominations_one"]

export interface ITradesMutator {
  insertGasTrade(): MutationResult<
    ITradesAddGasData,
    InsertGasTradeMutationResponse
  >

  insertElecTrade(): MutationResult<
    ITradesAddElecData,
    InsertElecTradeMutationResponse
  >

  confirmGasTrade(): MutationResult<
    ITradesConfirmGasData,
    ConfirmGasTradeMutationResponse
  >

  confirmElecTrade(): MutationResult<
    ITradesConfirmElecData,
    ConfirmElecTradeMutationResponse
  >
}

export class TradesMutatorClass implements ITradesMutator {
  insertGasTrade(): MutationResult<
    ITradesAddGasData,
    InsertGasTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.insertGasTrade(),
      serializerFn: (
        data: ITradesAddGasData,
      ): InsertGasTradeMutationVariables => {
        return {
          input: {
            id: data.id,
            Date: formatLocalDateTime(dayjs()),
            Period: data.period,
            Price: data.price,
            Index: data.index,
            Volume: data.volume,
            Date_From: formatLocalDateTime(data.dateFrom),
            Date_To: formatLocalDateTime(data.dateTo),
            CounterParty: data.counterParty,
            Trader: data.trader,
            Notes: data.notes,
          },
        }
      },
    })
  }

  insertElecTrade(): MutationResult<
    ITradesAddElecData,
    InsertElecTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.insertElecTrade(),
      serializerFn: (
        data: ITradesAddElecData,
      ): InsertElecTradeMutationVariables => {
        return {
          input: {
            id: data.id,
            Date: formatLocalDateTime(dayjs()),
            Product: data.product,
            Period: data.period,
            Price: data.price,
            Volume: data.volume,
            Invoice_Date_From: formatLocalDateTime(data.invoiceDateFrom),
            Invoice_Date_To: formatLocalDateTime(data.invoiceDateTo),
            Counterparty: data.counterParty,
            Trader: data.trader,
            Notes: data.notes,
            PercentOfBaseload: data.percentOfBaseload,
          },
        }
      },
    })
  }

  confirmGasTrade(): MutationResult<
    ITradesConfirmGasData,
    ConfirmGasTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.confirmGasTrade(),
      serializerFn: (
        data: ITradesConfirmGasData,
      ): ConfirmGasTradeMutationVariables => {
        return {
          id: data.id,
          ConfirmReceivedDate: formatLocalDateTime(data.confirmReceivedDate),
          ConfirmSignedBy: data.confirmSignedBy ?? "",
          ConfirmSignedDate: formatLocalDateTime(data.confirmSignedDate),
        }
      },
    })
  }

  confirmElecTrade(): MutationResult<
    ITradesConfirmElecData,
    ConfirmElecTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.confirmElecTrade(),
      serializerFn: (
        data: ITradesConfirmElecData,
      ): ConfirmElecTradeMutationVariables => {
        return {
          id: data.id,
          ConfirmReceivedDate: formatLocalDateTime(data.confirmReceivedDate),
          ConfirmSignedBy: data.confirmSignedBy ?? "",
          ConfirmSignedDate: formatLocalDateTime(data.confirmSignedDate),
        }
      },
    })
  }
  updateGasTrade(): MutationResult<
    ITradesAddGasData,
    UpdateGasTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.updateGasTrade(),
      serializerFn: (
        data: ITradesAddGasData,
      ): UpdateGasTradeMutationVariables => {
        return {
          id: data.id,
          CounterParty: data.counterParty,
          Date_From: formatLocalDateTime(data.dateFrom),
          Date_To: formatLocalDateTime(data.dateTo),
          Index: data.index,
          Period: data.period,
          Price: data.price,
          Volume: data.volume,
          Notes: data.notes,
        }
      },
    })
  }
  updateElecTrade(): MutationResult<
    ITradesAddElecData,
    UpdateElecTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.updateElecTrade(),
      serializerFn: (
        data: ITradesAddElecData,
      ): UpdateElecTradeMutationVariables => {
        return {
          id: data.id,
          Counterparty: data.counterParty,
          Invoice_Date_From: formatLocalDateTime(data.invoiceDateFrom),
          Invoice_Date_To: formatLocalDateTime(data.invoiceDateTo),
          Period: data.period,
          Price: data.price,
          Product: data.product,
          Volume: data.volume,
          PercentOfBaseload: data.percentOfBaseload,
          Notes: data.notes,
        }
      },
    })
  }
  insertGasTradeHistory(): MutationResult<
    ITradesAddGasHistoryData,
    InsertGasTradeHistoryMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.insertGasTradeHistory(),
      serializerFn: (
        data: ITradesAddGasHistoryData,
      ): InsertGasTradeHistoryMutationVariables => {
        return {
          input: {
            id: data.id,
            gas_trade_id: data.gasTradeId,
            Date: formatLocalDateTime(data.date),
            Period: data.period,
            Price: data.price,
            Index: data.index,
            Volume: data.volume,
            Date_From: formatLocalDateTime(data.dateFrom),
            Date_To: formatLocalDateTime(data.dateTo),
            CounterParty: data.counterParty,
            Trader: data.trader,
            ConfirmReceived: formatLocalDateTime(data.confirmReceivedDate),
            ConfirmSignedDate: formatLocalDateTime(data.confirmSignedDate),
            ConfirmSignedBy: data.confirmSignedBy,
            Notes: data.notes,
          },
        }
      },
    })
  }
  insertElecTradeHistory(): MutationResult<
    ITradesAddElecHistoryData,
    InsertElecTradeHistoryMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.insertElecTradeHistory(),
      serializerFn: (
        data: ITradesAddElecHistoryData,
      ): InsertElecTradeHistoryMutationVariables => {
        return {
          input: {
            id: data.id,
            elec_trade_id: data.elecTradeId,
            Date: formatLocalDateTime(data.date),
            Product: data.product,
            Period: data.period,
            Price: data.price,
            Volume: data.volume,
            Invoice_Date_From: formatLocalDateTime(data.invoiceDateFrom),
            Invoice_Date_To: formatLocalDateTime(data.invoiceDateTo),
            Counterparty: data.counterParty,
            Trader: data.trader,
            ConfirmReceivedDate: formatLocalDateTime(data.confirmReceivedDate),
            ConfirmSignedDate: formatLocalDateTime(data.confirmSignedDate),
            ConfirmSignedBy: data.confirmSignedBy,
            PercentOfBaseload: data.percentOfBaseload,
            Notes: data.notes,
          },
        }
      },
    })
  }
  unlockGasTrade(): MutationResult<
    { id: number },
    UnlockGasTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.unlockGasTrade(),
      serializerFn: (data: { id: number }): UnlockGasTradeMutationVariables => {
        return {
          id: data.id,
        }
      },
    })
  }
  unlockElecTrade(): MutationResult<
    { id: number },
    UnlockElecTradeMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.unlockElecTrade(),
      serializerFn: (data: {
        id: number
      }): UnlockElecTradeMutationVariables => {
        return {
          id: data.id,
        }
      },
    })
  }

  insertElecNominations(): MutationResult<
    IElecNominationsData,
    InsertElecNominationsMutationResponse
  > {
    return useMutation({
      ...HasuraMutations.trades.insertElecNominations(),
      serializerFn: (
        data: IElecNominationsData,
      ): InsertElecNominationsMutationVariables => {
        return {
          input: {
            Hour_1: getHalfHourlyVolume(data, [1, 2]),
            Hour_2: getHalfHourlyVolume(data, [3, 4]),
            Hour_3: getHalfHourlyVolume(data, [5, 6]),
            Hour_4: getHalfHourlyVolume(data, [7, 8]),
            Hour_5: getHalfHourlyVolume(data, [9, 10]),
            Hour_6: getHalfHourlyVolume(data, [11, 12]),
            Hour_7: getHalfHourlyVolume(data, [13, 14]),
            Hour_8: getHalfHourlyVolume(data, [15, 16]),
            Hour_9: getHalfHourlyVolume(data, [17, 18]),
            Hour_10: getHalfHourlyVolume(data, [19, 20]),
            Hour_11: getHalfHourlyVolume(data, [21, 22]),
            Hour_12: getHalfHourlyVolume(data, [23, 24]),
            Hour_13: getHalfHourlyVolume(data, [25, 26]),
            Hour_14: getHalfHourlyVolume(data, [27, 28]),
            Hour_15: getHalfHourlyVolume(data, [29, 30]),
            Hour_16: getHalfHourlyVolume(data, [31, 32]),
            Hour_17: getHalfHourlyVolume(data, [33, 34]),
            Hour_18: getHalfHourlyVolume(data, [35, 36]),
            Hour_19: getHalfHourlyVolume(data, [37, 38]),
            Hour_20: getHalfHourlyVolume(data, [39, 40]),
            Hour_21: getHalfHourlyVolume(data, [41, 42]),
            Hour_22: getHalfHourlyVolume(data, [43, 44]),
            Hour_23: getHalfHourlyVolume(data, [45, 46]),
            Hour_24: getHalfHourlyVolume(data, [47, 48]),
            Hour_25: getHalfHourlyVolume(data, [49, 50]),
            created_at: formatLocalDateTime(data.createdAt),
            updated_at: formatLocalDateTime(data.updatedAt),
            date: formatLocalDateTime(data.date),
            trader: data.trader,
          },
        }
      },
    })
  }
}

export const TradesMutator = new TradesMutatorClass()
